'use client';

import {
  useEffect,
  useCallback,
  useReducer,
  createContext,
  useContext,
  type PropsWithChildren,
} from 'react';
import { WebSocketClientType } from '../../types';
import { PricesWebSocketManager, TradesWebSocketManager } from '../../library';
import type { WebSocketManagerConfig } from '../../types';

// ============================================
// Types
// ============================================

interface IState {
  pricesConnection: WebSocket | null;
  tradesConnection: WebSocket | null;
}

interface IContext extends IState {
  dispatch: React.Dispatch<Action>;
}

type Action =
  | { type: 'UPDATE_PRICES_CONNECTION'; payload: { ws: WebSocket | null } }
  | { type: 'UPDATE_TRADES_CONNECTION'; payload: { ws: WebSocket | null } };

// ============================================
// Context & Initial State
// ============================================

const initState: IState = {
  pricesConnection: null,
  tradesConnection: null,
};

const WebSocketContext = createContext<IContext | undefined>(undefined);

// ============================================
// Reducer
// ============================================

function reducer(state: IState, action: Action) {
  switch (action.type) {
    case 'UPDATE_PRICES_CONNECTION': {
      return {
        ...state,
        pricesConnection: action.payload.ws,
      };
    }
    case 'UPDATE_TRADES_CONNECTION': {
      return {
        ...state,
        tradesConnection: action.payload.ws,
      };
    }
    default: {
      return state;
    }
  }
}

// ============================================
// Provider Props
// ============================================

export interface LegacyWebSocketProviderProps {
  /** Hash token for authentication */
  hashToken: string;
  /** Prices WebSocket URL */
  pricesWebSocketUrl?: string;
  /** Trades WebSocket URL */
  tradesWebSocketUrl?: string;
  /** WebSocket configuration */
  websocketConfig?: WebSocketManagerConfig;
  /** Callback when prices connection changes */
  onPricesConnectionSet?: (ws: WebSocket | null) => void;
  /** Callback when trades connection changes */
  onTradesConnectionSet?: (ws: WebSocket | null) => void;
  /** Whether the provider is enabled */
  enabled?: boolean;
}

// ============================================
// Legacy WebSocket Provider
// ============================================

/**
 * @deprecated Use SocketManagerProvider instead for new implementations.
 * This provider is kept for backwards compatibility.
 */
const WebSocketProvider = ({
  children,
  hashToken,
  pricesWebSocketUrl,
  tradesWebSocketUrl,
  websocketConfig = {},
  onPricesConnectionSet,
  onTradesConnectionSet,
  enabled = true,
}: PropsWithChildren<LegacyWebSocketProviderProps>) => {
  const [state, dispatch] = useReducer(reducer, initState);

  const setConnectCallback = useCallback(
    (ws: WebSocket | null, client: string) => {
      if (client === WebSocketClientType.PRICES) {
        dispatch({
          type: 'UPDATE_PRICES_CONNECTION',
          payload: { ws },
        });
        onPricesConnectionSet?.(ws);
      } else if (client === WebSocketClientType.TRADES) {
        dispatch({
          type: 'UPDATE_TRADES_CONNECTION',
          payload: { ws },
        });
        onTradesConnectionSet?.(ws);
      }
    },
    [onPricesConnectionSet, onTradesConnectionSet],
  );

  const initPricesSocket = useCallback(async () => {
    if (!pricesWebSocketUrl) return;

    try {
      const pricesConfig = {
        websocketUrl: pricesWebSocketUrl,
        socketId: WebSocketClientType.PRICES,
        websocketConfig,
      };

      PricesWebSocketManager.initializePricesSocketManager(
        pricesConfig,
        setConnectCallback,
        hashToken,
      );

      const pricesWsManager = PricesWebSocketManager.getPricesWSInstance();

      if (!pricesWsManager) {
        throw new Error('PricesWebSocketManager not initialized');
      }

      if (pricesWsManager.getPricesConnection()) {
        return;
      }

      await pricesWsManager.initializePriceConnection();
    } catch (error: unknown) {
      console.error(
        '[WebSocketProvider] Error initializing prices socket',
        error,
      );
    }
  }, [pricesWebSocketUrl, websocketConfig, setConnectCallback, hashToken]);

  const initTradesSocket = useCallback(async () => {
    if (!tradesWebSocketUrl) return;

    try {
      const tradesConfig = {
        websocketUrl: tradesWebSocketUrl,
        socketId: WebSocketClientType.TRADES,
        websocketConfig,
      };

      TradesWebSocketManager.initializeTradesSocketManager(
        tradesConfig,
        setConnectCallback,
        hashToken,
      );

      const tradesWsManager = TradesWebSocketManager.getTradesWSInstance();

      if (!tradesWsManager) {
        throw new Error('TradesWebSocketManager not initialized');
      }

      if (tradesWsManager.getTradesConnection()) {
        return;
      }

      await tradesWsManager.initializeTradesConnection();
    } catch (error: unknown) {
      console.error(
        '[WebSocketProvider] Error initializing trades socket',
        error,
      );
    }
  }, [tradesWebSocketUrl, websocketConfig, setConnectCallback, hashToken]);

  const initializeConnections = useCallback(async () => {
    if (!hashToken) {
      console.warn('[WebSocketProvider] Hash token not provided');
      return;
    }

    await Promise.all([initPricesSocket(), initTradesSocket()]);
  }, [hashToken, initPricesSocket, initTradesSocket]);

  useEffect(() => {
    if (enabled) {
      void initializeConnections();
    }

    return () => {
      const pricesWsManager = PricesWebSocketManager.getPricesWSInstance();
      if (pricesWsManager) {
        void pricesWsManager.disconnectPriceConnection();
      }

      const tradesWsManager = TradesWebSocketManager.getTradesWSInstance();
      if (tradesWsManager) {
        void tradesWsManager.disconnectTradesConnection();
      }
    };
  }, [initializeConnections, enabled]);

  return (
    <WebSocketContext.Provider
      value={{
        pricesConnection: state.pricesConnection,
        tradesConnection: state.tradesConnection,
        dispatch,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

/**
 * @deprecated Use useWebSocket from '@ind-money/socket-manager/hooks' instead.
 */
export const useLegacyWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error(
      'useLegacyWebSocket must be used within a WebSocketProvider',
    );
  }
  return context;
};

export default WebSocketProvider;
